import { useState } from 'react'
import { LogIn, Lock } from 'lucide-react'
import { SignInDialog } from '@/auth/SignInDialog'

/**
 * Full-section "paywall" rendered in place of a gated module when an
 * anonymous user navigates to it. Tabs in the header stay visible so
 * visitors see what they're missing; clicking through lands here with
 * a clear sign-in CTA and a brief value pitch for the module.
 *
 * Used by AppRoutes for Strategy Engine and Cash Flow — the two
 * marquee features behind the magic-link gate.
 */
export interface SignInGateModuleProps {
  /** Module label (e.g. "Strategy Engine"). */
  title: string
  /** One-line pitch shown in the gate. Keeps the user oriented. */
  pitch: string
  /** Bullets describing what the module offers — three is the sweet spot. */
  bullets: string[]
}

export function SignInGateModule({ title, pitch, bullets }: SignInGateModuleProps) {
  const [signInOpen, setSignInOpen] = useState(false)
  return (
    <div className="flex items-center justify-center py-12 sm:py-20 px-4">
      <div className="relative w-full max-w-lg bg-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8 text-center overflow-hidden">
        <div className="pointer-events-none absolute -top-24 -right-24 w-64 h-64 rounded-full bg-emerald-500/10 blur-2xl" />
        <div className="relative">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 mb-4">
            <Lock className="w-6 h-6" />
          </div>
          <h2 className="text-2xl font-bold text-white">{title}</h2>
          <p className="mt-2 text-sm sm:text-base text-slate-400">{pitch}</p>

          <ul className="mt-6 space-y-2 text-left max-w-sm mx-auto">
            {bullets.map((b) => (
              <li
                key={b}
                className="flex items-start gap-2.5 text-sm text-slate-300 leading-relaxed"
              >
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>

          <button
            onClick={() => setSignInOpen(true)}
            className="mt-7 inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-5 py-3 rounded-lg text-sm font-semibold transition-colors shadow-lg shadow-blue-600/20"
          >
            <LogIn className="w-4 h-4" />
            Sign in to unlock
          </button>
          <p className="mt-3 text-[11px] text-slate-500">
            Magic link, no password. Invite-only beta.
          </p>
        </div>
      </div>
      <SignInDialog open={signInOpen} onClose={() => setSignInOpen(false)} />
    </div>
  )
}
